// Canvas rendering perf helpers for DxfCanvas — viewport culling, per-frame render
// budgets, and cached entity geometry — pulled out on its own so the perf-sensitive
// bits (what to skip drawing, how much of it, and how to draw it cheaply) live in
// one place instead of being scattered inline in the draw loop.

import { bulgeArc, segmentCount, segmentEnds, tessellatePolyline } from './bulge.js'

// World-space bounding box per entity, cached by object identity — entities are
// replaced (not mutated) on edit, so a stale cache entry just falls out of scope
// with its old object and gets garbage-collected along with it.
const entityBoundsCache = new WeakMap()

// A bulge segment's true curve bulges away from the straight chord between its
// two vertices — a bbox built from vertices alone can understate an arc-heavy
// entity's real footprint (this fed entityScreenSize's handle-visibility cull
// below, silently hiding vertex grips on rounded shapes like stair treads).
// Expands `b` to the arc's full circle bbox — a safe over-approximation, cheap
// (no tessellation) and never smaller than the true curve.
function expandBoundsForArc(b, p1, p2, bulgeVal) {
  const arc = bulgeArc(p1, p2, bulgeVal)
  if (!arc) return
  const { cx, cy, r } = arc
  if (cx - r < b.minX) b.minX = cx - r
  if (cx + r > b.maxX) b.maxX = cx + r
  if (cy - r < b.minY) b.minY = cy - r
  if (cy + r > b.maxY) b.maxY = cy + r
}

export function getEntityBounds(e) {
  let b = entityBoundsCache.get(e)
  if (b !== undefined) return b
  if (e.type === 'LINE' && e.start && e.end) {
    b = { minX: Math.min(e.start[0], e.end[0]), maxX: Math.max(e.start[0], e.end[0]),
          minY: Math.min(e.start[1], e.end[1]), maxY: Math.max(e.start[1], e.end[1]) }
  } else if (e.type === 'CIRCLE' && e.center) {
    b = { minX: e.center[0]-e.radius, maxX: e.center[0]+e.radius,
          minY: e.center[1]-e.radius, maxY: e.center[1]+e.radius }
  } else if (e.type === 'LWPOLYLINE' && e.points?.length) {
    let minX=Infinity, maxX=-Infinity, minY=Infinity, maxY=-Infinity
    for (const [px, py] of e.points) {
      if (px < minX) minX = px; if (px > maxX) maxX = px
      if (py < minY) minY = py; if (py > maxY) maxY = py
    }
    b = { minX, maxX, minY, maxY }
    if (e.bulge?.length) {
      for (let si = 0; si < segmentCount(e.points, e.closed); si++) expandBoundsForArc(b, ...segmentEnds(e.points, si), e.bulge[si])
    }
  } else {
    b = null
  }
  entityBoundsCache.set(e, b)
  return b
}

// Visible world-space rect (+ margin) for the current canvas/transform — anything
// outside this doesn't need to touch the ctx at all.
export function getVisibleWorldBounds(canvas, t, marginPx = 50) {
  return {
    minX: (-marginPx - t.offsetX) / t.scale,
    maxX: (canvas.width + marginPx - t.offsetX) / t.scale,
    maxY: (t.offsetY + marginPx) / t.scale,
    minY: (t.offsetY - canvas.height - marginPx) / t.scale,
  }
}

export function boundsOutsideView(b, view) {
  return !!b && (b.maxX < view.minX || b.minX > view.maxX || b.maxY < view.minY || b.minY > view.maxY)
}

// Hard ceiling on vertex-drag handles drawn in a single frame. Each handle is 4
// ctx calls (beginPath/arc/fill/stroke); viewport culling already keeps this low
// for normal zoomed-in editing, but if the whole drawing is visible at once (e.g.
// "select all" while zoomed to fit) culling can't reduce the count — this bounds
// the worst case instead of letting it scale with total selected vertex count.
export const MAX_VERTEX_HANDLES_PER_FRAME = 4000

// Handles are drawn at a fixed *screen* size (radius ~6px) regardless of zoom, so
// when zoomed far out, consecutive vertices of a dense polyline can be only a
// couple of screen pixels apart — they render as one solid blob of overlapping
// circles instead of distinct, useful drag handles. worldToScreen + this spacing
// let the caller skip a vertex whose handle would visually collide with the last
// one actually drawn along the same polyline.
export const MIN_VERTEX_HANDLE_SPACING_PX = 10

export function worldToScreen(wx, wy, t) {
  return [wx * t.scale + t.offsetX, t.offsetY - wy * t.scale]
}

export function getStableScreenAnchor(canvas, t) {
  const screenX = canvas.width / 2
  const screenY = canvas.height / 2
  return {
    screenX,
    screenY,
    worldX: (screenX - t.offsetX) / t.scale,
    worldY: (t.offsetY - screenY) / t.scale,
  }
}

export function worldToScreenStable(wx, wy, t, anchor) {
  return [
    anchor.screenX + (wx - anchor.worldX) * t.scale,
    anchor.screenY - (wy - anchor.worldY) * t.scale,
  ]
}

// Union of the entities' bounds in canvas px (stable-anchor space), or null if none has any.
export function getEntitiesScreenBounds(entities, t, anchor) {
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
  for (const e of entities) {
    const b = getEntityBounds(e)
    if (!b) continue
    if (b.minX < minX) minX = b.minX
    if (b.maxX > maxX) maxX = b.maxX
    if (b.minY < minY) minY = b.minY
    if (b.maxY > maxY) maxY = b.maxY
  }
  if (minX === Infinity) return null
  const [sx0, sy1] = worldToScreenStable(minX, minY, t, anchor)
  const [sx1, sy0] = worldToScreenStable(maxX, maxY, t, anchor)
  return { minX: sx0, maxX: sx1, minY: sy0, maxY: sy1 }
}

export function getEntityScreenTransform(e, t, anchor, dx = 0, dy = 0) {
  const local = getEntityLocalPath(e)
  if (!local) return null
  const [sx, sy] = worldToScreenStable(local.originX + dx, local.originY + dy, t, anchor)
  return {
    path: local.path,
    matrix: [t.scale, 0, 0, -t.scale, sx, sy],
  }
}

const WHEEL_DELTA_LINE_PX = 16
const WHEEL_DELTA_PAGE_PX = 800
const WHEEL_ZOOM_PX_PER_DOUBLE = 360
const MIN_WHEEL_ZOOM_FACTOR = 0.5
const MAX_WHEEL_ZOOM_FACTOR = 2
export const MIN_CANVAS_ZOOM = 0.01
export const MAX_CANVAS_ZOOM = 100

export function getWheelZoomFactor(event) {
  const modeMultiplier = event.deltaMode === 1
    ? WHEEL_DELTA_LINE_PX
    : event.deltaMode === 2
      ? WHEEL_DELTA_PAGE_PX
      : 1
  const deltaPx = event.deltaY * modeMultiplier
  const factor = 2 ** (-deltaPx / WHEEL_ZOOM_PX_PER_DOUBLE)
  return Math.max(MIN_WHEEL_ZOOM_FACTOR, Math.min(MAX_WHEEL_ZOOM_FACTOR, factor))
}

export function applyZoomAtScreenPoint(
  t,
  screenX,
  screenY,
  factor,
  minScale = MIN_CANVAS_ZOOM,
  maxScale = MAX_CANVAS_ZOOM,
) {
  const currentScale = t.scale > 0 ? t.scale : 1
  // Fit-to-screen can land outside the limits; widen them to the current scale so
  // zoom moves smoothly back toward the range instead of jumping onto its edge.
  const lo = Math.min(minScale, currentScale)
  const hi = Math.max(maxScale, currentScale)
  const nextScale = Math.max(lo, Math.min(hi, currentScale * factor))
  const appliedFactor = nextScale / currentScale
  return {
    scale: nextScale,
    offsetX: screenX + (t.offsetX - screenX) * appliedFactor,
    offsetY: screenY + (t.offsetY - screenY) * appliedFactor,
  }
}

// Below this on-screen size (in px, along its longer side), an entity's own
// footprint isn't meaningfully bigger than a single handle — individual vertices
// can't be told apart or grabbed anyway, so its handles are skipped entirely
// rather than drawn as an indistinguishable cluster. This is screen-size based
// (not a raw zoom/scale cutoff) so it behaves the same regardless of the DXF's
// world units (cm/mm/inch/unitless all render at whatever `t.scale` maps to px).
export const MIN_ENTITY_SCREEN_SIZE_FOR_HANDLES_PX = 32

export function entityScreenSize(b, t) {
  return b ? Math.max((b.maxX - b.minX) * t.scale, (b.maxY - b.minY) * t.scale) : Infinity
}

export function isViewportPanning(pan) {
  return !!pan && !pan.boxMode && (!!pan.middleBtn || !!pan.moved)
}

export const MIN_WHEEL_ENTITY_SCREEN_SIZE_PX = 0.5

export function shouldRenderEntityInWheelFrame(b, t, important = false) {
  return important || entityScreenSize(b, t) >= MIN_WHEEL_ENTITY_SCREEN_SIZE_PX
}

// Local Path2D per entity, cached by object identity — same replace-not-mutate
// invalidation as getEntityBounds: an edit produces a new entity object, so a
// stale cached path just falls out of scope with its old object. draw() was
// rebuilding this geometry (beginPath/moveTo/lineTo/...) from scratch for every
// visible entity on every single frame; building it once and replaying it via
// ctx.stroke(path) is much cheaper for entities with many points.
//
// The path commands are stored relative to a nearby entity origin instead of in
// absolute DXF coordinates. Some production files sit hundreds of millions of
// units away from world zero; feeding those huge coordinates directly to canvas
// while zooming causes browser precision loss and visible entities disappear.
const entityPathCache = new WeakMap()

export function getEntityLocalPath(e) {
  let cached = entityPathCache.get(e)
  if (cached !== undefined) return cached
  let path, originX, originY
  if (e.type === 'LINE' && e.start && e.end) {
    path = new Path2D()
    originX = e.start[0]
    originY = e.start[1]
    path.moveTo(0, 0)
    path.lineTo(e.end[0] - originX, e.end[1] - originY)
  } else if (e.type === 'CIRCLE' && e.center) {
    path = new Path2D()
    originX = e.center[0]
    originY = e.center[1]
    path.arc(0, 0, e.radius, 0, Math.PI * 2)
  } else if (e.type === 'LWPOLYLINE' && e.points?.length > 1) {
    const [first, ...rest] = tessellatePolyline(e.points, e.bulge, e.closed)
    path = new Path2D()
    originX = first[0]
    originY = first[1]
    path.moveTo(0, 0)
    for (const [x, y] of rest) path.lineTo(x - originX, y - originY)
    if (e.closed) path.closePath()
  } else {
    cached = null
    entityPathCache.set(e, cached)
    return cached
  }
  cached = { path, originX, originY }
  entityPathCache.set(e, cached)
  return cached
}
